import { HttpEvent, HttpRequest, HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn } from '@angular/common/http';
import { EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsService } from 's1m-angular-bootstrap-lib';

import { AccessEventsService } from 'src/app/auth/services/access-events.service';
import { BackendErrorResponse } from '../shared/models/backend-response.model';
import { AccountService } from '../auth/services/account.service';
import { LocalStorageService } from '../shared/services/local-storage.service';

interface WhitelistItem {
  reqUrl: string;
  reqMethod: string;
  errorStatusCode?: number;
}

interface HttpErrorTranslation {
  language: string;
  description: string;
}

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const whitelist: WhitelistItem[] = [
    { reqUrl: '/Account/Token', reqMethod: 'POST', errorStatusCode: 400 },
    { reqUrl: '/Account/ChangePassword', reqMethod: 'POST', errorStatusCode: 400 },
  ];

  const environmentInjector = inject(EnvironmentInjector);
  return next(req).pipe(
    retry({
      count: 1,
      delay: (error: HttpErrorResponse) => retryNotifier(error, req),
    }),
    catchError((error: HttpErrorResponse) => handleError(req, error, whitelist, environmentInjector)),
  );
};

const retryNotifier = (error: HttpErrorResponse, request: HttpRequest<any>): Observable<any> => {
  if (!String(error.status).startsWith('4') && request.method !== 'PUT' && request.method !== 'POST') {
    return timer(1000);
  }
  throw error;
};

const handleError = (
  request: HttpRequest<any>,
  error: HttpErrorResponse,
  whitelist: WhitelistItem[],
  environmentInjector: EnvironmentInjector,
): Observable<never> => {
  runInInjectionContext(environmentInjector, () => {
    const token = inject(LocalStorageService).token;
    const accountService = inject(AccountService);
    const isRefreshTokenExpired = accountService.isExpired(token?.refreshTokenExpires);
    const isTokenExpired = accountService.isExpired(token?.expiration);
    if (error.status === 401 && isRefreshTokenExpired) {
      onAuthExpiredError();
    } else if (
      !isOnWhitelist(request.url, request.method, error.status, whitelist) &&
      (error.status !== 401 || (error.status === 401 && !isTokenExpired))
    ) {
      onOtherError(error);
    }
  });
  return throwError(() => error);
};

const onAuthExpiredError = () => {
  const translateService = inject(TranslateService);
  const errorTitle = translateService.instant('common.authExpired');
  const errorMessage = translateService.instant('common.credentialsExpired');
  inject(AccessEventsService).logout();
  inject(Router).navigate(['/login']);
  const notificationsService = inject(NotificationsService);
  notificationsService.clearAll();
  notificationsService.addError(errorMessage, errorTitle);
};

const onOtherError = (httpError: HttpErrorResponse) => {
  const translateService = inject(TranslateService);
  const defaultErrorTitle: string = httpError.error
    ? httpError.error.descriptions
      ? getLanguageErrorDescription(httpError.error.descriptions)
      : translateService.instant('common.errorCode', { code: httpError.status })
    : httpError.status
    ? translateService.instant('common.errorCode', { code: httpError.status })
    : undefined;
  const defaultErrorMsg = httpError.error
    ? httpError.error.message
    : httpError.message || translateService.instant('common.unidentifiedError');
  let errorTitle = defaultErrorTitle;
  let errorMessage = defaultErrorMsg;
  const apiError: BackendErrorResponse = httpError.error;
  if (httpError.status === 400 && apiError) {
    errorTitle = apiError.type || defaultErrorTitle;
    errorMessage = apiError.description || defaultErrorMsg;
  }
  inject(NotificationsService).addError(errorMessage, errorTitle);
};

const getLanguageErrorDescription = (descriptions: HttpErrorTranslation[]) => {
  return (
    descriptions.find(
      (description: HttpErrorTranslation) => description.language.slice(0, 2) === inject(TranslateService).currentLang,
    )?.description || ''
  );
};

const isOnWhitelist = (url: string, method: string, errorStatusCode: number, whitelist: WhitelistItem[]): boolean => {
  return whitelist.some((wlItem) => {
    const urlAndMethodAreEqual = url.endsWith(wlItem.reqUrl) && method === wlItem.reqMethod;
    const hasErrorCode = wlItem.errorStatusCode !== undefined;
    return hasErrorCode ? urlAndMethodAreEqual && wlItem.errorStatusCode === errorStatusCode : urlAndMethodAreEqual;
  });
};
